// Overlaying an English translation onto a transcript the console already renders.
//
// The backend translates FIELDS, not messages: every translatable string in the
// transcript is extracted with a `<message id>#<field>` id (backend
// `recruiter/conversation_translation.py`), because the prose an operator reads
// is spread across a message's `text`, its `title`/`description` fallbacks, and —
// for a widget row — Mira's question, the options she offered, and the answer the
// client picked, which live in the untyped `metadata` bag. Translating `text`
// alone leaves the rows where the conversation actually turned in the original
// language.
//
// So the overlay is a flat id → English map and the render stays exactly as it
// was: each place that prints a string asks the overlay for its coordinate first
// and falls back to the original. That fallback is the important half — a
// translation is allowed to come back partial (a chunk whose model call failed, a
// transcript past the size ceiling), and a partial one must degrade to the
// original words rather than to a blank bubble.
import type { ConversationTranslation } from "@recruiter/shared/types";

/** Which field of a message a translated unit belongs to. `widget.option.<n>`
 *  carries its index, so it is built rather than listed. */
export type TranslationField =
  | "text"
  | "title"
  | "description"
  | "widget.question"
  | "widget.answer";

export type TranslationOverlay = ReadonlyMap<string, string>;

export function unitId(messageId: string, field: TranslationField): string {
  return `${messageId}#${field}`;
}

export function optionUnitId(messageId: string, index: number): string {
  return `${messageId}#widget.option.${index}`;
}

/** The id → English map for a fetched translation. */
export function buildOverlay(translation: ConversationTranslation): TranslationOverlay {
  const map = new Map<string, string>();
  for (const unit of translation.units ?? []) {
    if (unit.id && unit.text) map.set(unit.id, unit.text);
  }
  return map;
}

/**
 * The English reading of one field, or the original when there is none.
 *
 * `original` is passed through untouched whenever the overlay is absent, the
 * field was not translated, or the field is empty — so a caller can wrap every
 * string it renders without branching on whether translation is on.
 */
export function translated(
  overlay: TranslationOverlay | null,
  id: string,
  original: string | null | undefined,
): string {
  const source = original ?? "";
  if (!overlay) return source;
  const trimmed = source.trim();
  if (!trimmed) return source;
  return overlay.get(id) ?? source;
}

export type TranslationStatus = "idle" | "pending" | "ready" | "error";

/**
 * Which state the Translate control is in.
 *
 * **A translation already on screen wins over everything else.** The control that
 * gets an operator back to the ORIGINAL only renders in `ready`, so letting a
 * later failure short-circuit to `error` would take that control away while the
 * overlay was still applied, trapping a reader of an audit trail in the
 * translated view. A failure that happens next to a loaded translation is a line
 * of text beside it, not a state that replaces it.
 *
 * `pending` beats `error` for the same reason in the other direction: a retry in
 * flight is more informative than the failure it is already answering.
 */
export function translationStatus(input: {
  hasData: boolean;
  pending: boolean;
  error: boolean;
}): TranslationStatus {
  if (input.hasData) return "ready";
  if (input.pending) return "pending";
  if (input.error) return "error";
  return "idle";
}

/**
 * What to tell the operator about a translation that came back incomplete, or
 * null when it is whole.
 *
 * Untranslated rows look exactly like rows that were already English, so an
 * incomplete result reads as a working feature unless it says otherwise. The
 * counts are fields, not messages, because that is what the backend bounds.
 */
export function translationShortfall(translation: ConversationTranslation): string | null {
  const parts: string[] = [];
  if (translation.droppedUnits > 0) {
    parts.push(
      `the oldest ${translation.droppedUnits} ${plural(translation.droppedUnits, "part")} of this conversation (too long to translate in one go)`,
    );
  }
  // Split by whether trying again could help. An overflow is our own output
  // ceiling and will land in the same place on identical input, so telling an
  // operator to retry it would be telling them to spend for the same result.
  const modelMissed = translation.missingUnits - translation.overflowedUnits;
  if (modelMissed > 0) {
    parts.push(`${modelMissed} ${plural(modelMissed, "part")} the model did not return`);
  }
  if (translation.overflowedUnits > 0) {
    parts.push(
      `${translation.overflowedUnits} ${plural(translation.overflowedUnits, "part")} in a stretch too long for one pass (translating again will not change this)`,
    );
  }
  if (translation.truncatedUnits > 0) {
    parts.push(
      `${translation.truncatedUnits} very long ${plural(translation.truncatedUnits, "message")}, translated up to a limit`,
    );
  }
  if (parts.length === 0) return null;
  return `Still in the original language: ${parts.join(", and ")}.`;
}

function plural(n: number, word: string): string {
  return n === 1 ? word : `${word}s`;
}
